import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Badge, Loading } from "@/components/kit";

export default function Insights() {
  const { activeStore } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = () => {
    if (!activeStore) return;
    setLoading(true);
    setError(null);
    api.get("/insights", { params: { store_id: activeStore } })
      .then(({ data }) => setData(data))
      .catch((e) => setError(e.response?.data?.detail || "Could not generate insights"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [activeStore]);

  return (
    <div>
      <PageHeader
        title="AI Insights"
        subtitle="Natural-language explanations of tomorrow's forecast — generated from your actual data"
        right={<button data-testid="refresh-insights" onClick={load} className="inline-flex items-center gap-2 border border-zinc-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-zinc-50 transition-colors duration-200"><RefreshCw className="h-4 w-4" /> Regenerate</button>}
      />

      {error ? (
        <Card className="p-6 bg-red-50/60 border-red-200 max-w-3xl">
          <div className="text-sm text-red-800">{error}</div>
        </Card>
      ) : loading || !data ? (
        <Loading label="Analysing forecast variables…" />
      ) : (
        <div className="space-y-4 max-w-3xl">
          <div className="flex items-center gap-2">
            <Badge level={data.source === "llm" ? "success" : "info"}>{data.source === "llm" ? "AI generated" : "Rule-based"}</Badge>
            <Badge level={data.vs_normal_pct >= 0 ? "success" : "warning"}>{data.vs_normal_pct >= 0 ? "+" : ""}{data.vs_normal_pct}% vs normal</Badge>
          </div>
          {data.insights.map((text, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
              <Card className="p-5 border-amber-200 bg-gradient-to-br from-amber-50/60 to-white" testid={`insight-${i}`}>
                <div className="flex gap-3">
                  <div className="h-8 w-8 rounded-lg bg-amber-100 grid place-items-center shrink-0">
                    <Sparkles className="h-4 w-4 text-amber-600" />
                  </div>
                  <p className="text-[15px] leading-relaxed text-zinc-800">{text}</p>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
